import { useRef, useState } from 'react'

export type ChatSession = {
	id: string
	title: string
	messages: ChatMessage[]
	createdAt: Date
}

export type ChatMessage = {
	id: string
	role: 'user' | 'assistant'
	content: string
	isComplete: boolean
}

export const useChatSessions = () => {
	const [sessions, setSessions] = useState<ChatSession[]>([])
	const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
	const activeSessionIdRef = useRef<string | null>(null)

	const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null

	const setActiveSession = (id: string | null) => {
		activeSessionIdRef.current = id
		setActiveSessionId(id)
	}

	const createSession = () => {
		const id = crypto.randomUUID()
		setSessions((prev) => [...prev, { id, title: 'New Chat', messages: [], createdAt: new Date() }])
		setActiveSession(id)

		return id
	}

	const selectSession = (id: string) => setActiveSession(id)

	const goToList = () => setActiveSession(null)

	const deleteSession = (id: string) => setSessions((prev) => prev.filter((s) => s.id !== id))

	const addUserMessage = (content: string) => {
		const sessionId = activeSessionIdRef.current

		if (!sessionId) {
			return null
		}

		const messageId = crypto.randomUUID()

		const newMessage: ChatMessage = {
			id: messageId,
			role: 'user',
			content,
			isComplete: true,
		}

		updateSession(sessionId, (session) => {
			const isFirstUserMessage = !session.messages.some((message) => message.role === 'user')

			return {
				...session,
				messages: [...session.messages, newMessage],
				title: isFirstUserMessage ? content.slice(0, 40) : session.title,
			}
		})

		return messageId
	}

	const addAssistantMessage = (content: string, isComplete: boolean, sessionId?: string) => {
		const targetSessionId = sessionId ?? activeSessionIdRef.current

		if (!targetSessionId) {
			return null
		}

		const messageId = crypto.randomUUID()
		const newMessage: ChatMessage = { id: messageId, role: 'assistant', content, isComplete }

		updateSession(targetSessionId, (session) => ({
			...session,
			messages: [...session.messages, newMessage],
		}))

		return messageId
	}

	const appendMessage = (messageId: string, content: string, sessionId?: string) => {
		const targetSessionId = sessionId ?? activeSessionIdRef.current

		if (!targetSessionId) {
			return
		}

		updateSession(targetSessionId, (session) => ({
			...session,
			messages: updateMessage(session.messages, messageId, (message) => ({
				...message,
				content: message.content + content,
			})),
		}))
	}

	const completeMessage = (messageId: string, sessionId?: string) => {
		const targetSessionId = sessionId ?? activeSessionIdRef.current

		if (!targetSessionId) {
			return
		}

		updateSession(targetSessionId, (session) => ({
			...session,
			messages: updateMessage(session.messages, messageId, (message) => ({ ...message, isComplete: true })),
		}))
	}

	const updateSession = (sessionId: string, updater: (session: ChatSession) => ChatSession) => {
		setSessions((prev) => prev.map((session) => (session.id === sessionId ? updater(session) : session)))
	}

	const updateMessage = (messages: ChatMessage[], messageId: string, updater: (message: ChatMessage) => ChatMessage) =>
		messages.map((message) => (message.id === messageId ? updater(message) : message))

	return {
		sessions,
		activeSession,
		activeSessionId,
		createSession,
		selectSession,
		goToList,
		deleteSession,
		addUserMessage,
		addAssistantMessage,
		appendMessage,
		completeMessage,
	}
}
